/**
 * The ONE definition of a test fixture account (#6188; moved to the shared package so the client's pickers and the
 * server use the same rule). Use it in every reader that lists, counts or
 * messages people, so a QA login never leaks into a roster, headcount, AI context or alert.
 *
 * Email: our QA logins are "test", "e2e-test" or "test" + a separator + a suffix ("test.admin@",
 * "test-qa@", "test+p12@") at the company domain. The domain is required on purpose: an external
 * "test@customer.com" is a real contact, not our fixture. Display name: the fixture names QA uses.
 */
const COMPANY_DOMAIN = "onpointgen.com";
const TEST_LOCAL_PART = /^(?:e2e-)?test(?:[._+-][^@]*)?$/i;
const TEST_NAMES: ReadonlySet<string> = new Set(["test (fixture)", "test admin", "e2e test"]);

export function isTestAccountEmail(email: string | null | undefined): boolean {
  if (typeof email !== "string") return false;
  const value = email.trim();
  const at = value.lastIndexOf("@");
  return at > 0 && value.slice(at + 1).toLowerCase() === COMPANY_DOMAIN && TEST_LOCAL_PART.test(value.slice(0, at));
}

export function isTestFixtureName(name: string | null | undefined): boolean {
  return typeof name === "string" && TEST_NAMES.has(name.trim().toLowerCase());
}

export function isTestFixtureUser(user: { email?: string | null; displayName?: string | null; name?: string | null }): boolean {
  return isTestAccountEmail(user.email) || isTestFixtureName(user.displayName) || isTestFixtureName(user.name);
}

/**
 * Nick's ruling (2026-10-08): "yes hide the test ones including e2e". Fixture domains that exist only for QA, and
 * any `e2e-` local part. DELIBERATELY not folded into `isTestAccountEmail` above: that one keeps its company-domain
 * scope because server alert filtering and report audiences depend on it. Only `isTestAccountUser` (the picker rule)
 * uses this.
 */
const FIXTURE_DOMAINS: ReadonlySet<string> = new Set(["quoter-saas.test", "test.com", "officehub-test.invalid"]);
const E2E_LOCAL_PART = /^e2e-/i;

export function isFixtureEmail(email: string | null | undefined): boolean {
  if (typeof email !== "string") return false;
  const value = email.trim();
  const at = value.lastIndexOf("@");
  if (at <= 0) return false;
  return FIXTURE_DOMAINS.has(value.slice(at + 1).toLowerCase()) || E2E_LOCAL_PART.test(value.slice(0, at));
}

/**
 * Any record that can say it is a test account: an `isTestAccount: true` marker (stamped by the test-logins tool
 * on the logins it creates, and by Nick's per-account decision) OR the email/name rule above.
 *
 * The marker alone is not enough today (it is set on 2 accounts; the email rule catches 16 more, the fixture-domain /
 * `e2e-` rule 3 more), so the answer is the UNION. Unknown shapes are NOT test accounts:
 * hiding a real person from a picker is the worse error.
 */
export function isTestAccountUser(user: {
  email?: string | null;
  displayName?: string | null;
  name?: string | null;
  isTestAccount?: unknown;
} | null | undefined): boolean {
  if (!user) return false;
  return user.isTestAccount === true || isTestFixtureUser(user) || isFixtureEmail(user.email);
}
